<template #default>
  <div class="rate-modal">
    <a-modal
      class="page-modal"
      v-model:visible="modalVisible"
      :title="isEdit ? 'Edit ' + localForm.assetClass : 'Add Index'"
      :style="{ top: '32px', width: '900px' }"
      @ok="handleOk"
      @cancel="handleCancel"
      :confirmLoading="loading"
      destroyOnClose
    >
      <a-form
        ref="addFormRef"
        :model="localForm"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        :rules="rules"
      >
        <a-card class="rate-card" title="Basics" :bordered="false">
          <a-row :gutter="16">
            <a-col :span="12" v-if="isEdit">
              <a-form-item label="SecurityId">
                <a-input
                  v-model:value="localForm.securityId"
                  :disabled="isEdit"
                />
              </a-form-item>
            </a-col>
            <!--  <a-col :span="12" v-if="isEdit">
              <a-form-item label="ExternalSecurityId">
                <a-input
                  v-model:value="localForm.externalSecurityId"
                  :disabled="isEdit"
                />
              </a-form-item>
            </a-col> -->
            <a-col :span="12">
              <a-form-item label="Type" name="rateType">
                <a-select
                  v-model:value="localForm.rateType"
                  allow-clear
                  :show-search="true"
                  :filter-option="filterOption"
                >
                  <a-select-option
                    v-for="item in typeCodeOptions"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Inst Class">
                <a-select
                  v-model:value="localForm.instrumentClass"
                  allow-clear
                  :show-search="true"
                  :filter-option="filterOption"
                  :disabled="isEdit"
                >
                  <a-select-option
                    v-for="item in instClassOptions"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Name" name="desc">
                <a-input v-model:value="localForm.desc" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="BB_TCM" name="ticker">
                <a-input v-model:value="localForm.ticker" :disabled="isEdit" />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Sec Type">
                <SecTypeInput v-model:value="localForm.securityType" />
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>

        <a-card class="rate-card" title="Settings" :bordered="false">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="Basket Type">
                <a-select
                  allow-clear
                  :show-search="true"
                  :filter-option="filterOption"
                  disabled
                >
                  <a-select-option value="Benchmark">Benchmark</a-select-option>
                  <a-select-option value="Model">Model</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="PnL Ccy">
                <a-select
                  v-model:value="localForm.pnlCurrency"
                  allow-clear
                  :show-search="true"
                  :filter-option="filterOption"
                >
                  <a-select-option
                    v-for="item in currencyList"
                    :key="item.currencyCode"
                    :value="item.currencyCode"
                  >
                    {{ item.currencyCode }} - {{ item.currencyDesc }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Term" name="maturityTerm">
                <a-select
                  v-model:value="localForm.maturityTerm"
                  allow-clear
                  :show-search="true"
                  :filter-option="filterOption"
                >
                  <a-select-option
                    v-for="item in termCodeOptions"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Status">
                <a-select
                  v-model:value="localForm.isActive"
                  style="width: 100%"
                >
                  <a-select-option :value="true">ACTIVE</a-select-option>
                  <a-select-option :value="false">INACTIVE</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
        </a-card>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, inject, watch } from 'vue'
import { useStore } from 'vuex'
import SecTypeInput from '@/components/SecTypeInput.vue'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'

const store = useStore()

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  addForm: {
    type: Object,
    required: true,
  },
})

const labelCol = { span: 6, style: { textAlign: 'left' } }
const wrapperCol = { span: 18 }

const typeCodeOptions = inject('typeCodeOptions', ref([]))
const termCodeOptions = inject('termCodeOptions', ref([]))
const security = inject('security')

const currencyList = computed(() => {
  const currencies = store.getters['reference/getCurrencyList'] || []
  return [...currencies].sort((a, b) =>
    a.currencyCode.localeCompare(b.currencyCode)
  )
})

const instClassOptions = ref(['RATE'])
const addFormRef = ref(null)
const loading = ref(false)
const rules = {
  desc: [{ required: true, message: 'Please input name' }],
  ticker: [{ required: true, message: 'Please input BB_TCM' }],
  maturityTerm: [{ required: true, message: 'Please select Term' }],
  rateType: [{ required: true, message: 'Please select Type' }],
}

const emit = defineEmits(['update:visible', 'update:security'])

const modalVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
})

const userName = computed(() => store.getters['auth/getUserName'] || 'Guest')
const localForm = ref(props.addForm)

watch(
  () => props.addForm,
  (newVal) => {
    localForm.value = newVal
  },
  {
    immediate: true,
  }
)

const filterOption = (input, option) => {
  const key = option?.key ?? option?.value ?? ''
  return String(key).toLowerCase().includes(input.toLowerCase())
}

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const formatDateTime = (val) =>
  val ? dayjs(val).format('YYYY-MM-DDTHH:mm:ss') : null

const buildPayload = () => {
  const payload = {
    ...localForm.value,
    name: localForm.value.desc,
    exchange: { ...localForm.value.exchange },
    identifiers: [],
    maturityDate: formatDateTime(localForm.value.maturityDate),
    lastModifiedOn: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
    lastModifiedBy: userName.value,
  }

  if (payload.ticker) {
    payload.identifiers.push({
      type: 'BB_TCM',
      value: payload.ticker,
    })
  }

  return payload
}

const handleOk = async () => {
  try {
    await addFormRef.value?.validate()
  } catch (error) {
    message.warning(getErrorMessage(error, 'Please complete required fields'))
    return
  }

  loading.value = true
  try {
    const payload = buildPayload()
    if (props.isEdit) {
      await security.updateSec(payload)
      message.success('Security updated successfully')
    } else {
      await security.createSec(payload)
      message.success('Security created successfully')
    }
    modalVisible.value = false
  } catch (error) {
    message.error(
      getErrorMessage(
        error,
        props.isEdit ? 'Failed to update security' : 'Failed to create security'
      )
    )
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  modalVisible.value = false
}
</script>

<style scoped>
.rate-card {
  margin-bottom: 12px;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.06);
}
.rate-card :deep(.ant-card-head-title) {
  font-weight: 600;
  letter-spacing: 0.2px;
}
.rate-card :deep(.ant-card-body) {
  padding-top: 8px;
}
</style>
